import { selectedPlaybookSchema } from "@repo/app-types/agents/selectedPlaybook";
import { agentInstructionSectionCandidateDraftSchema } from "@repo/app-types/entities/configurationAgent";
import { configurationTestResultIdSchema } from "@repo/app-types/entities/configurationTestResult";
import {
  buildPlaybookProposalResourceKey,
  type CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import { teammateIdSchema } from "@repo/app-types/entities/teammate";
import type { UIMessage } from "ai";
import { z } from "zod";

/** How the AI SDK prefixes a statically-declared tool part's `type`. */
const TOOL_PART_TYPE_PREFIX = "tool-";

/**
 * The proposals one assistant turn put up for review, in the order the agent
 * made them.
 *
 * Each turn lists what it proposed rather than everything outstanding, which
 * is what keeps the transcript readable as a history: a proposal appears once,
 * where it was made, and its row carries whatever became of it. A turn that
 * proposed nothing — no proposal calls, or only tests that measured a node
 * without changing it — gets no list.
 */
export function selectTurnProposals(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): readonly CopilotProposalSummary[] {
  if (message.role !== "assistant") {
    return [];
  }

  // One row per target: a turn that revised its own proposal shows the last
  // one it made. Earlier turns keep their own rows.
  const byResourceKey = new Map<string, CopilotProposalSummary>();
  for (const proposalId of readProposalIds(message)) {
    const proposal = proposalsById.get(proposalId);
    if (!proposal) {
      continue;
    }
    byResourceKey.set(proposal.resource_key, proposal);
  }
  return [...byResourceKey.values()];
}

/**
 * The saved test results a test tool returns: enough to label the row, open
 * it in the editor, and tell whether the tested resource has changed since.
 * Read off the completed call's output — unlike a proposal, a test result has
 * no live status to look up. Each resource type's test tool returns its own
 * shape, so each has its own arm, tagged with the resource type it measured.
 */
const playbookTurnTestResultSchema = z
  .object({
    test_result_id: configurationTestResultIdSchema,
    playbook: selectedPlaybookSchema,
    playbook_node_id: z.string(),
    playbook_revision_id: z.string(),
    node_name: z.string(),
  })
  .transform((result) => ({ resource_type: "playbook" as const, ...result }));

const teammateAgentTurnTestResultSchema = z
  .object({
    test_result_id: configurationTestResultIdSchema,
    teammate_agent_id: teammateIdSchema,
    teammate_agent_config_revision_id: z.string().nullable(),
    candidate_draft: agentInstructionSectionCandidateDraftSchema.nullable(),
  })
  .transform((result) => ({
    resource_type: "teammateAgent" as const,
    ...result,
  }));

const turnTestResultSchema = z.union([
  playbookTurnTestResultSchema,
  teammateAgentTurnTestResultSchema,
]);

export type PlaybookTurnTestResult = z.infer<
  typeof playbookTurnTestResultSchema
>;
export type TeammateAgentTurnTestResult = z.infer<
  typeof teammateAgentTurnTestResultSchema
>;
export type TurnTestResult = z.infer<typeof turnTestResultSchema>;

/**
 * The test results one assistant turn saved, in the order it ran them. A
 * proposal's output carries no `test_result_id`, so proposals never show
 * here. A test the same turn turned into a proposal is left out: the proposal
 * carries the same grades on its own row (see `checkIsTestReusedByProposal`).
 */
export function selectTurnTestResults(
  message: UIMessage,
  proposalsById: ReadonlyMap<string, CopilotProposalSummary>,
): readonly TurnTestResult[] {
  if (message.role !== "assistant") {
    return [];
  }
  const proposals = selectTurnProposals(message, proposalsById);
  const results: TurnTestResult[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const parsed = turnTestResultSchema.safeParse(
      "output" in part ? part.output : undefined,
    );
    if (!parsed.success) {
      continue;
    }
    if (!checkIsTestReusedByProposal(parsed.data, proposals)) {
      results.push(parsed.data);
    }
  }
  return results;
}

/** Whether a proposal the same turn made carries this test's grades. */
function checkIsTestReusedByProposal(
  testResult: TurnTestResult,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  switch (testResult.resource_type) {
    case "playbook":
      return checkIsPlaybookTestReusedByProposal(testResult, proposals);
    case "teammateAgent":
      return checkIsTeammateAgentTestReusedByProposal(testResult, proposals);
  }
}

/** A playbook node test is reused by a proposal for that node. */
function checkIsPlaybookTestReusedByProposal(
  testResult: PlaybookTurnTestResult,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  const nodeKey = buildPlaybookProposalResourceKey(
    testResult.playbook.id,
    testResult.playbook_node_id,
  );
  return proposals.some((proposal) => proposal.resource_key === nodeKey);
}

/**
 * A voice agent instruction check is reused by an instruction proposal for
 * that agent. A test case proposal does not come from a check, so a check
 * run alongside one keeps its row.
 */
function checkIsTeammateAgentTestReusedByProposal(
  testResult: TeammateAgentTurnTestResult,
  proposals: readonly CopilotProposalSummary[],
): boolean {
  return proposals.some(
    (proposal) =>
      proposal.resource_type === "teammateAgent" &&
      proposal.kind === "update_instruction_section" &&
      proposal.resource.id === testResult.teammate_agent_id,
  );
}

/**
 * How many proposals the thread's turns have made. The agent writes proposals
 * server-side mid-turn, which no client mutation observes, so the chat watches
 * this count to know when its copy of them is stale.
 */
export function countTurnProposals(messages: readonly UIMessage[]): number {
  let total = 0;
  for (const message of messages) {
    if (message.role === "assistant") {
      total += readProposalIds(message).length;
    }
  }
  return total;
}

/**
 * Whether a message part is a finished tool call. Tool parts are typed either
 * as `dynamic-tool` or as `tool-${name}`, and both carry `state`.
 */
function isCompletedToolPart(part: UIMessage["parts"][number]): boolean {
  const isTool =
    part.type.startsWith(TOOL_PART_TYPE_PREFIX) || part.type === "dynamic-tool";
  return isTool && "state" in part && part.state === "output-available";
}

/**
 * The `proposal_id`s a turn's proposal tools returned. A proposal is recognized
 * by the shape of a completed call's output — a string `proposal_id`, or a
 * `proposals` list of them from the voice agent instructions tool, which
 * writes one row per tile so the tiles share the checks of the combined
 * candidate — rather than by the tool's name, so the list keeps working
 * across tool renames and for any future proposal-writing tool. A proposal is
 * persisted only for a real change, so every returned `proposal_id` is a
 * suggestion to show.
 */
function readProposalIds(message: UIMessage): readonly string[] {
  const ids: string[] = [];
  for (const part of message.parts) {
    if (!isCompletedToolPart(part)) {
      continue;
    }
    const output = ("output" in part ? part.output : undefined) as
      | { proposal_id?: unknown; proposals?: unknown }
      | undefined;
    if (typeof output?.proposal_id === "string") {
      ids.push(output.proposal_id);
    }
    if (Array.isArray(output?.proposals)) {
      for (const entry of output.proposals as { proposal_id?: unknown }[]) {
        if (typeof entry?.proposal_id === "string") {
          ids.push(entry.proposal_id);
        }
      }
    }
  }
  return ids;
}
